import { useRef, useState, useEffect } from "react";
import { Play, ArrowRight } from "lucide-react";

const VIDEO_SRC = "/apresentacao.mp4"; // PLACEHOLDER — Junior vai subir o arquivo em public/
const PERCENTUAL_LIBERAR = 0.95;

export default function ApresentacaoVideoGate() {
  const videoRef = useRef(null);
  const infoRef = useRef(null);
  const [iniciado, setIniciado] = useState(false);
  const [liberado, setLiberado] = useState(false);
  const [maxVisto, setMaxVisto] = useState(0); // segundo máximo realmente alcançado
  const [maisInfoAberto, setMaisInfoAberto] = useState(false);
  const [videoErro, setVideoErro] = useState(false);

  // Rastreia tempo real assistido (não aceita seek pra frente — se pular, maxVisto não avança)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => {
      // Só avança maxVisto se o tempo atual é "próximo" do maxVisto anterior
      // (margem de 1.5s pra aceitar reprodução natural, mas rejeitar saltos grandes)
      if (video.currentTime <= maxVisto + 1.5) {
        setMaxVisto((prev) => Math.max(prev, video.currentTime));
      } else {
        // Pulou — volta pro ponto máximo visto
        video.currentTime = maxVisto;
      }
    };

    const onEnded = () => {
      // Checa se a cobertura real é suficiente
      if (video.duration > 0 && maxVisto / video.duration >= PERCENTUAL_LIBERAR) {
        setLiberado(true);
      }
    };

    const onLoadedMetadata = () => {
      // Em cada tick, verifica se já alcançou o percentual
      // (libera antes de o vídeo acabar se a pessoa assistiu 95%+)
    };

    const onError = () => {
      setVideoErro(true);
    };

    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("ended", onEnded);
    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("error", onError);

    return () => {
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("error", onError);
    };
  }, [maxVisto]);

  // Libera o botão quando o percentual é atingido (não precisa esperar ended)
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    if (maxVisto / video.duration >= PERCENTUAL_LIBERAR) {
      setLiberado(true);
    }
  }, [maxVisto]);

  const handleIniciar = () => {
    const video = videoRef.current;
    if (!video) return;
    setIniciado(true);
    video.play().catch(() => {
      // Autoplay bloqueado — o usuário terá que clicar no próprio controle do vídeo
    });
  };

  const handleMaisInfo = () => {
    setMaisInfoAberto(true);
    setTimeout(() => {
      infoRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6">
      {/* Cabeçalho */}
      <div className="text-center mb-10 md:mb-14">
        <span className="inline-block text-sm font-semibold text-green-400 uppercase tracking-wider mb-3">
          Apresentação
        </span>
        <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white tracking-tight mb-4">
          Conheça o Evolzap em{" "}
          <span className="bg-gradient-to-r from-green-400 to-emerald-300 bg-clip-text text-transparent">
            poucos minutos
          </span>
        </h1>
        <p className="text-lg md:text-xl text-zinc-400 leading-relaxed max-w-2xl mx-auto">
          Assista ao vídeo abaixo e descubra como o Evolzap pode transformar o atendimento e as vendas do seu negócio.
        </p>
      </div>

      {/* Vídeo com overlay inicial */}
      <div className="relative rounded-2xl overflow-hidden bg-[#111111] border border-white/10 shadow-2xl shadow-green-500/5 aspect-video mb-6">
        <video
          ref={videoRef}
          src={VIDEO_SRC}
          className="w-full h-full"
          controls={iniciado}
          playsInline
          preload="metadata"
          onError={() => setVideoErro(true)}
        />
        {!iniciado && !videoErro && (
          <button
            type="button"
            onClick={handleIniciar}
            className="absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-3 bg-black/70 hover:bg-black/80 transition-colors group"
            aria-label="Clique para assistir o vídeo"
          >
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-green-500 group-hover:bg-green-400 flex items-center justify-center shadow-lg shadow-green-500/40 transition-colors">
              <Play className="w-10 h-10 md:w-12 md:h-12 text-black fill-black ml-1" />
            </div>
            <span className="text-white font-semibold text-lg md:text-xl">
              Clique para assistir o vídeo
            </span>
          </button>
        )}
        {videoErro && (
          <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-3 bg-black/80 px-6 text-center">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
              <Play className="w-10 h-10 md:w-12 md:h-12 text-zinc-600 ml-1" />
            </div>
            <span className="text-white font-semibold text-lg md:text-xl">
              Vídeo em breve
            </span>
            <span className="text-zinc-400 text-sm md:text-base max-w-md">
              O vídeo de apresentação será disponibilizado em breve. Volte em instantes.
            </span>
          </div>
        )}
      </div>

      {/* Progresso + CTA */}
      <div className="text-center">
        {iniciado && !liberado && (
          <p className="text-sm text-zinc-500 mb-4">
            Continue assistindo para desbloquear mais informações
          </p>
        )}
        <button
          type="button"
          onClick={handleMaisInfo}
          disabled={!liberado}
          className={
            liberado
              ? "inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-black font-semibold px-6 py-3.5 rounded-xl shadow-lg shadow-green-500/20 hover:scale-[1.02] transition-all"
              : "inline-flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-zinc-500 font-medium px-6 py-3.5 rounded-xl cursor-not-allowed"
          }
        >
          Clique aqui para mais informações
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>

      {/* Seção "Mais Informações" — hidden até liberar */}
      {maisInfoAberto && (
        <div ref={infoRef} className="mt-20 md:mt-28 pt-10 border-t border-white/10 animate-fade-in">
          <div className="text-center mb-12">
            <span className="inline-block text-sm font-semibold text-green-400 uppercase tracking-wider mb-3">
              Sobre o Evolzap
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white tracking-tight mb-4">
              Pra quem é o Evolzap
            </h2>
            <p className="text-lg md:text-xl text-zinc-400 leading-relaxed max-w-2xl mx-auto">
              {/* PLACEHOLDER: Junior vai fornecer o texto exato aqui */}
              O Evolzap é para pequenos e médios negócios que querem organizar o atendimento no WhatsApp, automatizar vendas e crescer com Inteligência Artificial. Ideal para pizzarias, salões, barbearias, assistências técnicas, lava-jatos, mercadinhos, lojas de vestuário, provedores de internet e profissionais autônomos.
            </p>
          </div>

          {/* Preços */}
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight mb-6">
              Valor da mensalidade
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              <div className="bg-[#111111] border border-white/10 rounded-xl p-5">
                <div className="text-zinc-400 text-sm font-semibold uppercase tracking-wider mb-2">Gratuito</div>
                <div className="text-3xl font-extrabold text-white">R$ 0</div>
                <div className="text-xs text-zinc-500 mt-1">para conhecer</div>
              </div>
              <div className="bg-[#1A1A1A] border border-green-500/40 rounded-xl p-5 relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-black text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                  Mais popular
                </div>
                <div className="text-green-400 text-sm font-semibold uppercase tracking-wider mb-2">Start</div>
                <div className="text-3xl font-extrabold text-white">R$ 99,90<span className="text-base font-normal text-zinc-500">/mês</span></div>
                <div className="text-xs text-zinc-500 mt-1">para começar a operar</div>
              </div>
              <div className="bg-[#111111] border border-white/10 rounded-xl p-5">
                <div className="text-zinc-400 text-sm font-semibold uppercase tracking-wider mb-2">Pro</div>
                <div className="text-3xl font-extrabold text-white">R$ 249,90<span className="text-base font-normal text-zinc-500">/mês</span></div>
                <div className="text-xs text-zinc-500 mt-1">para equipes que escalam</div>
              </div>
            </div>
          </div>

          {/* CTA final */}
          <div className="text-center">
            <a
              href="https://evolzap.shop"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-black font-semibold px-8 py-4 rounded-xl shadow-lg shadow-green-500/20 hover:scale-[1.02] transition-all text-lg"
            >
              Começar agora
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
